import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { Layers, Plus, Pencil, Trash2, Loader2, Search, MapPin } from 'lucide-react';
import { clientSubRegionsService, clientAccessService } from '../services';
import { getApiError } from '@/lib/utils';

export default function ClientSubRegionsPanel({ clientId, clientName }) {
  const [subRegions, setSubRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allSites, setAllSites] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignSelected, setAssignSelected] = useState(new Set());
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [srData, sitesData] = await Promise.all([
        clientSubRegionsService.list(clientId),
        clientAccessService.listSites({ limit: 1000 }),
      ]);
      setSubRegions(srData.items || []);
      // Filter sites belonging to this client group
      const clientSites = sitesData.filter(s => s.group_id === clientId);
      setAllSites(clientSites);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [clientId, toast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await clientSubRegionsService.create({ client_id: clientId, name: newName.trim() });
      setNewName('');
      setShowCreate(false);
      toast({ title: 'Sub-region created' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err), variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await clientSubRegionsService.update(id, { name: editName.trim() });
      setEditingId(null);
      toast({ title: 'Renamed' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err), variant: 'destructive' });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete sub-region "${name}"? Client users assigned to it will lose access to its sites.`)) return;
    try {
      await clientSubRegionsService.remove(id);
      toast({ title: 'Deleted' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err), variant: 'destructive' });
    }
  };

  const openAssignModal = (sr) => {
    setAssignTarget(sr);
    setAssignSelected(new Set((sr.sites || []).map(s => s.id)));
    setAssignSearch('');
    setAssignModalOpen(true);
  };

  const handleAssignSave = async () => {
    if (!assignTarget) return;
    setAssignSaving(true);
    try {
      await clientSubRegionsService.updateSites(assignTarget.id, Array.from(assignSelected));
      toast({ title: `Sites updated for ${assignTarget.name}` });
      setAssignModalOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err), variant: 'destructive' });
    } finally { setAssignSaving(false); }
  };

  const filteredAssignSites = allSites.filter(s =>
    !assignSearch || s.name.toLowerCase().includes(assignSearch.toLowerCase())
  );

  if (loading) return <Card><CardContent className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading sub-regions...</CardContent></Card>;

  return (
    <>
      <Card data-testid="client-sub-regions-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-5 w-5 text-violet-600" />
                Sub-Regions
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Organise {clientName}'s {allSites.length} sites into named regions for easier portal access management.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-sub-region-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />New Sub-Region
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Create inline */}
          {showCreate && (
            <div className="flex gap-2 mb-4 p-3 bg-violet-50 rounded-lg border border-violet-200">
              <Input placeholder="Sub-region name (e.g. Scotland & NE)" value={newName} onChange={e => setNewName(e.target.value)}
                className="flex-1 h-9" autoFocus onKeyDown={e => e.key === 'Enter' && handleCreate()} data-testid="new-sub-region-name" />
              <Button size="sm" onClick={handleCreate} disabled={creating || !newName.trim()} data-testid="save-sub-region-btn">
                {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          )}

          {subRegions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No sub-regions yet.</p>
              <p className="text-xs mt-1">Create one to organise sites for regional manager access.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {subRegions.map(sr => (
                <div key={sr.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/30 transition-colors" data-testid={`sub-region-${sr.id}`}>
                  {editingId === sr.id ? (
                    <div className="flex gap-2 flex-1">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-8 flex-1"
                        autoFocus onKeyDown={e => e.key === 'Enter' && handleRename(sr.id)} />
                      <Button size="sm" variant="outline" className="h-8" onClick={() => handleRename(sr.id)}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{sr.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {sr.site_count} site{sr.site_count !== 1 ? 's' : ''}
                          {sr.sites?.length > 0 && (
                            <span className="ml-1 text-slate-400">
                              ({sr.sites.slice(0, 3).map(s => s.name).join(', ')}{sr.sites.length > 3 ? `, +${sr.sites.length - 3}` : ''})
                            </span>
                          )}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openAssignModal(sr)} data-testid={`assign-sites-${sr.id}`}>
                        <MapPin className="h-3 w-3 mr-1" />Manage Sites
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingId(sr.id); setEditName(sr.name); }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(sr.id, sr.name)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Sites Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-violet-600" />
              Manage Sites — {assignTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search sites..." value={assignSearch} onChange={e => setAssignSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{assignSelected.size} of {allSites.length} selected</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setAssignSelected(new Set(allSites.map(s => s.id)))}>Select All</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setAssignSelected(new Set())}>Clear</Button>
            </div>
          </div>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-1">
              {filteredAssignSites.map(site => (
                <label key={site.id} className={`flex items-center gap-2.5 p-2 rounded cursor-pointer transition-colors ${assignSelected.has(site.id) ? 'bg-violet-50' : 'hover:bg-muted/50'}`}>
                  <Checkbox checked={assignSelected.has(site.id)} onCheckedChange={() => {
                    setAssignSelected(prev => { const n = new Set(prev); n.has(site.id) ? n.delete(site.id) : n.add(site.id); return n; });
                  }} />
                  <span className="text-sm">{site.name}</span>
                </label>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignSave} disabled={assignSaving} data-testid="save-assign-sites-btn">
              {assignSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save ({assignSelected.size} sites)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
